import { Routes, Route, Navigate } from 'react-router-dom';
import { RIDES_ROLES } from '@actschurch/ccms-sdk';
import { RidesShell } from '@/layouts/RidesShell';
import { RequireAuth } from '@/components/auth/RequireAuth';
import { RequireRole } from '@/components/auth/RequireRole';
import { RidesProfileGate } from '@/components/auth/RidesProfileGate';
import { MaintenanceGate, RequireRegistrationOpen, RequireSecurityView } from '@/components/auth/SettingsGates';
import LandingPage from '@/pages/Landing';
import PrivacyPage from '@/pages/Privacy';
import TermsPage from '@/pages/Terms';
import HomePage from '@/pages/Home';
import AccountPage from '@/pages/Account';
import NotFound from '@/pages/NotFound';
import LoginPage from '@/pages/auth/login';
import RegisterPage from '@/pages/auth/register';
import ForgotPasswordPage from '@/pages/auth/forgot-password';
import ResetPasswordPage from '@/pages/auth/reset-password';
import ConfirmEmailPage from '@/pages/auth/confirm-email';
import PlanningCentreCallbackPage from '@/pages/auth/planning-centre-callback';
import RideEventPage from '@/pages/ride/Event';
import RideRoutePage from '@/pages/ride/Route';
import RidePartyPage from '@/pages/ride/Party';
import RideMatchesPage from '@/pages/ride/Matches';
import RideVerifyPage from '@/pages/ride/Verify';
import RideConfirmPage from '@/pages/ride/Confirm';
import RideDayPage from '@/pages/ride/Day';
import DriveProfilePage from '@/pages/drive/Profile';
import DriveVerifyPage from '@/pages/drive/Verify';
import DriveEventsPage from '@/pages/drive/Events';
import DriveRoutePage from '@/pages/drive/Route';
import DriveSummaryPage from '@/pages/drive/Summary';
import DriveListingsPage from '@/pages/drive/Listings';
import DriveDayPage from '@/pages/drive/Day';
import SecurityPage from '@/pages/security';

const SECURITY_ROLES = [RIDES_ROLES.RidesSecurity, RIDES_ROLES.RidesAdmin, RIDES_ROLES.Admin];

/** Authenticated + member profile (DOB / gender / mobile) complete. Paused by settings.maintenanceMode. */
function Member({ children }: { children: React.ReactNode }) {
  return (
    <MaintenanceGate>
      <RequireAuth>
        <RidesProfileGate>{children}</RidesProfileGate>
      </RequireAuth>
    </MaintenanceGate>
  );
}

/** Member + the rider wizard is open (settings.riderRegistrationOpen). */
function Rider({ children }: { children: React.ReactNode }) {
  return <Member><RequireRegistrationOpen role="rider">{children}</RequireRegistrationOpen></Member>;
}

/** Member + the driver wizard is open (settings.driverRegistrationOpen). */
function Driver({ children }: { children: React.ReactNode }) {
  return <Member><RequireRegistrationOpen role="driver">{children}</RequireRegistrationOpen></Member>;
}

/**
 * One URL per step. Each step validates its prerequisites from the wizard store and
 * redirects to the earliest missing step (see riderMissingStep / driverMissingStep).
 */
export default function App() {
  return (
    <Routes>
      <Route element={<RidesShell />}>
        {/* Public */}
        <Route path="/" element={<MaintenanceGate><LandingPage /></MaintenanceGate>} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/register" element={<RegisterPage />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/confirm-email" element={<ConfirmEmailPage />} />
        <Route path="/auth/oauth/planning-centre/callback" element={<PlanningCentreCallbackPage />} />

        {/* Member */}
        {/* Home skips the maintenance gate on purpose: booked rides stay readable, and Home itself hides new bookings and changes. */}
        <Route path="/home" element={<RequireAuth><RidesProfileGate><HomePage /></RidesProfileGate></RequireAuth>} />
        <Route path="/account" element={<MaintenanceGate><RequireAuth><AccountPage /></RequireAuth></MaintenanceGate>} />

        {/* Rider wizard */}
        <Route path="/ride" element={<Navigate to="/ride/event" replace />} />
        <Route path="/ride/event" element={<Rider><RideEventPage /></Rider>} />
        <Route path="/ride/route" element={<Rider><RideRoutePage /></Rider>} />
        <Route path="/ride/party" element={<Rider><RidePartyPage /></Rider>} />
        <Route path="/ride/matches" element={<Rider><RideMatchesPage /></Rider>} />
        <Route path="/ride/verify" element={<Rider><RideVerifyPage /></Rider>} />
        <Route path="/ride/confirm/:bookingId" element={<Member><RideConfirmPage /></Member>} />
        <Route path="/ride/day/:bookingId" element={<Member><RideDayPage /></Member>} />

        {/* Driver wizard */}
        <Route path="/drive" element={<Navigate to="/drive/profile" replace />} />
        <Route path="/drive/profile" element={<Driver><DriveProfilePage /></Driver>} />
        <Route path="/drive/verify" element={<Driver><DriveVerifyPage /></Driver>} />
        <Route path="/drive/events" element={<Driver><DriveEventsPage /></Driver>} />
        <Route path="/drive/route" element={<Driver><DriveRoutePage /></Driver>} />
        <Route path="/drive/summary" element={<Driver><DriveSummaryPage /></Driver>} />
        <Route path="/drive/listings" element={<Member><DriveListingsPage /></Member>} />
        <Route path="/drive/day/:availabilityId" element={<Member><DriveDayPage /></Member>} />

        {/* Campus security (settings.securityViewEnabled) */}
        <Route path="/security" element={<MaintenanceGate><RequireSecurityView><RequireAuth><RequireRole roles={SECURITY_ROLES}><SecurityPage /></RequireRole></RequireAuth></RequireSecurityView></MaintenanceGate>} />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
